import { Box, Flex, Icon, RadioGroup, Text, Textarea, TextInput } from '@televet/kibble-ui';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import LoadingIndicator from 'shared/components/LoadingIndicator';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  LoyaltyProgramStatus,
  useFindOrCreateLoyaltyProgramMutation,
  useUpdateLoyaltyProgramSettingsMutation,
} from 'shared/types/graphql';
import isEqual from 'lodash-es/isEqual';
import { UnsavedChangesProps } from 'pages/LoyaltyProgram';
import { STATUSES, useCustomToast } from 'shared/hooks/useCustomToast';

interface LoyaltyProgramSettings {
  id?: string;
  description?: string;
  status?: LoyaltyProgramStatus;
  centsPerPoint?: number;
  allowSpendCarryOver?: boolean;
  minimumTransactionCents?: string;
}

interface IStatuses {
  children: JSX.Element;
  value: LoyaltyProgramStatus;
}

const statuses: IStatuses[] = [
  {
    children: (
      <Box>
        <Text as="p">Off</Text>
        <Text size="xs" variant="subtle">
          Loyalty Program will not show to clients.
        </Text>
      </Box>
    ),
    value: LoyaltyProgramStatus.Draft,
  },
  {
    children: (
      <Box>
        <Text as="p">Test Mode</Text>
        <Text size="xs" variant="subtle">
          Loyalty Program will show in Flow but will not show to clients.
        </Text>
      </Box>
    ),
    value: LoyaltyProgramStatus.Test,
  },
  {
    children: (
      <Box>
        <Text as="p">On</Text>
        <Text size="xs" variant="subtle">
          Loyalty Program will show to both clinic and client.
        </Text>
      </Box>
    ),
    value: LoyaltyProgramStatus.Live,
  },
];

const centsToDollars = (cents?: number, withDecimals?: boolean): string => {
  const amount = cents ? cents / 100 : 0;
  return withDecimals ? amount.toFixed(2) : `${amount}`;
};

const dollarsToCents = (dollars?: string | number): number => {
  return !!dollars ? Number(dollars) * 100 : 0;
};

const MAX_DESCRIPTION_CHAR_COUNT = 100;

const Settings = forwardRef(({ setHasUnsavedChange, setAreChangesValid }: UnsavedChangesProps, ref): JSX.Element => {
  const { toastMessagePop } = useCustomToast();
  const { currentClinicId } = useClinicUser();
  const [initialSettings, setInitialSettings] = useState<LoyaltyProgramSettings>();
  const [settings, setSettings] = useState<LoyaltyProgramSettings>();

  const [saveSettings] = useUpdateLoyaltyProgramSettingsMutation({
    variables: {
      data: {
        description: settings?.description,
        status: settings?.status,
        centsPerPoint: settings?.centsPerPoint,
        allowSpendCarryOver: settings?.allowSpendCarryOver,
        minimumTransactionCents: dollarsToCents(settings?.minimumTransactionCents),
      },
      where: {
        id: settings?.id,
      },
    },
  });

  useImperativeHandle(ref, () => ({
    onLeavingCurrentPage(): void {
      setInitialSettings(undefined);
      setSettings(undefined);
    },
    onClickedSaveButton(): void {
      saveSettings()
        .then(() => {
          toastMessagePop();
          getSettings();
        })
        .catch(() => {
          toastMessagePop(
            'Whoops! There was a problem saving your changes, please refresh the page and try again.',
            STATUSES.error,
          );
        });
    },
    onClickedCancelButton(): void {
      setSettings(initialSettings);
    },
  }));

  const [getSettings] = useFindOrCreateLoyaltyProgramMutation({
    onCompleted: (data) => {
      if (data.findOrCreateLoyaltyProgram) {
        const { findOrCreateLoyaltyProgram } = data;
        let { description } = findOrCreateLoyaltyProgram;

        if (!description.length) {
          description = 'Collect treats to earn rewards! For every $10 you spend, earn 1 treat.';
        }

        const minimumTransactionDollars = centsToDollars(findOrCreateLoyaltyProgram.minimumTransactionCents, true);

        setSettings({ ...findOrCreateLoyaltyProgram, description, minimumTransactionCents: minimumTransactionDollars });
        setInitialSettings({
          ...findOrCreateLoyaltyProgram,
          description,
          minimumTransactionCents: minimumTransactionDollars,
        });
      }
    },
    onError: (error) => {
      console.error(error);
    },
  });

  useEffect(() => {
    if (currentClinicId) {
      getSettings();
    }
  }, [currentClinicId, getSettings]);

  useEffect(() => {
    if (settings && initialSettings) {
      setHasUnsavedChange(isEqual(settings, initialSettings) ? false : true);
    }
  }, [initialSettings, setHasUnsavedChange, settings]);

  const earnings = [
    {
      children: (
        <Box>
          <Text as="p">Total Customer Spend</Text>
          <Text size="xs" variant="subtle">
            Earn 1 treat for every ${centsToDollars(settings?.centsPerPoint, true)} of total spend, rounding down.
            Leftover spending will be counted towards the next transaction.
          </Text>
        </Box>
      ),
      value: 1, // true
    },
    {
      children: (
        <Box>
          <Text as="p">Per Transaction</Text>
          <Text size="xs" variant="subtle">
            Earn 1 treat for every ${centsToDollars(settings?.centsPerPoint, true)} of total spend, rounding down, with
            a minimum required amount specified below. Leftover spending will be discarded.
          </Text>
        </Box>
      ),
      value: 0, // false
    },
  ];

  return settings ? (
    <Box maxW={['100%', '530px']}>
      <Box mb={8}>
        <Text as="p" fontWeight="bold" mb={2}>
          Loyalty Program Status
        </Text>
        <RadioGroup
          name="status"
          radios={statuses}
          direction="column"
          spacing={4}
          value={settings.status}
          onChange={(value: string): void => {
            setSettings((currentSettings) => {
              return { ...currentSettings, status: value as LoyaltyProgramStatus };
            });
          }}
        />
      </Box>
      <Box mb={8}>
        <Text as="p" fontWeight="bold" mb={2}>
          Exchange Rate
        </Text>
        <Flex alignItems="center" mb={3}>
          <Icon name="dollar" size="sm" />
          <Text as="p" size="lg" fontWeight="bold">
            {centsToDollars(settings.centsPerPoint, true)}
          </Text>
        </Flex>
        <Text size="sm">For every ${centsToDollars(settings.centsPerPoint)} spent a client will earn 1 treat.</Text>
      </Box>
      <Box mb={8}>
        <Text as="p" fontWeight="bold" mb={2}>
          Earnings
        </Text>
        <Text as="p" size="sm" variant="subtle" mb={3}>
          Determine how treat earnings are calculated.
        </Text>
        <RadioGroup
          name="allowSpendCarryOver"
          value={Number(settings.allowSpendCarryOver)}
          radios={earnings}
          direction="column"
          onChange={(value: string): void => {
            const val = value === '0' ? false : true;
            setSettings((currentSettings) => {
              return { ...currentSettings, allowSpendCarryOver: val };
            });
          }}
        />
        <Flex mt={2} ml={6} alignItems="center">
          <Text fontWeight="bold" size="sm">
            Minimum transaction amount:
          </Text>
          <TextInput
            name="minimumTransactionCents"
            leftElement={<Icon name="dollar" size="sm" />}
            size="sm"
            formControlStyle={{ width: '100px', ml: 1 }}
            type="number"
            isDisabled={settings.allowSpendCarryOver}
            value={settings.minimumTransactionCents}
            onKeyPress={(e): void => {
              if (e.key === '-' || e.key === 'e') {
                e.preventDefault();
              }
            }}
            onChange={(e): void => {
              setSettings({ ...settings, minimumTransactionCents: e.target.value });
            }}
          />
        </Flex>
      </Box>
      <Box>
        <Text as="p" fontWeight="bold" mb={2}>
          Loyalty Program Description
        </Text>
        <Text as="p" size="sm" variant="subtle" mb={3}>
          This description will show to customers in the Pet Portal.
        </Text>
        <Textarea
          name="description"
          helperText={`${settings.description?.length}/${MAX_DESCRIPTION_CHAR_COUNT}`}
          size="sm"
          isInvalid={
            (settings.description?.length || 0) > MAX_DESCRIPTION_CHAR_COUNT ||
            (settings.description?.length || 0) === 0
          }
          value={settings.description}
          onChange={(e): void => {
            const { value } = e.target;
            setSettings({ ...settings, description: value });
            if (value.length === 0 || value.length > MAX_DESCRIPTION_CHAR_COUNT) {
              setAreChangesValid(false);
            } else {
              setAreChangesValid(true);
            }
          }}
          orientation="vertical"
          formControlStyle={{ maxW: '350px' }}
          errorText="Description can't be empty or above 100 characters"
        />
      </Box>
    </Box>
  ) : (
    <LoadingIndicator />
  );
});

export default Settings;
